import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";

import { ItemCategoryInfoList } from "@/constants/const";
import { BusinessListType } from "@/types/Business/BusinessListType";
import { BusinessProfile } from "@/types/BusinessProfile";
import { getInventoryItemInfoById } from "@/helper/profile";
import { getCityCashProfit } from "@/helper/contractFunctions/MafiaGameBank";

export interface BusinessState {
  isLoadingBusinessList: boolean;
  businessList: BusinessListType[];

  businessProfile: BusinessProfile | null;
  loadingBusinessProfile: boolean;
}

const initialState: BusinessState = {
  isLoadingBusinessList: false,
  businessList: [],

  businessProfile: null,
  loadingBusinessProfile: false,
};

export const getBusinessList = createAsyncThunk(
  "business/getBusinessList",
  async () => {
    const response = await axios.get(`${SERVER_URL}/business`);
    return response.data;
  }
);

export const getBusinessProfileAction = createAsyncThunk(
  "business/getBusinessProfileAction",
  async ({ itemId, cityId }: { itemId: number; cityId: number }) => {
    const response = await axios.get(
      `${SERVER_URL}/business/detail?itemId=${itemId}&cityId=${cityId}`
    );

    if (itemId === 42) {
      response.data.profit = await getCityCashProfit(cityId);
    }

    return response.data;
  }
);

export const businessSlice = createSlice({
  name: "business",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(getBusinessList.pending, (state, { payload }) => {
      state.isLoadingBusinessList = true;
    });
    builder.addCase(getBusinessList.fulfilled, (state, { payload }) => {
      state.isLoadingBusinessList = false;

      state.businessList = payload.map((data: any) => {
        const inventoryItemInfo = getInventoryItemInfoById(data.item_id);

        return {
          id: data.id,
          userId: data.user_id,
          address: data.address,
          itemId: data.item_id,
          categoryId: inventoryItemInfo?.categoryId,
          typeId: inventoryItemInfo?.typeId,
          city: data.city,
          removed: data.removed,
          status: data.status ? 1 : 0,
          enabledStatus: ItemCategoryInfoList[
            inventoryItemInfo?.categoryId || 0
          ]?.activeStatus![inventoryItemInfo?.typeId || 0]
            ? 1
            : 0,
          timestamp: data.timestamp,
          ownerName: data.ownerName,
          familyInfo: {
            id: data.familyInfo?.id || -1,
            name: data.familyInfo?.name || "",
            logo: data.familyInfo?.logo || "",
          },
          profit: data.profit,
        };
      }) as BusinessListType[];
    });
    builder.addCase(getBusinessList.rejected, (state) => {
      state.isLoadingBusinessList = false;
    });

    builder.addCase(getBusinessProfileAction.pending, (state) => {
      state.loadingBusinessProfile = true;
    });
    builder.addCase(
      getBusinessProfileAction.fulfilled,
      (state, { payload }) => {
        state.loadingBusinessProfile = false;
        state.businessProfile = {
          id: payload.id,
          userId: payload.user_id,
          address: payload.address,
          itemId: payload.item_id,
          categoryId: getInventoryItemInfoById(payload.item_id)?.categoryId,
          typeId: getInventoryItemInfoById(payload.item_id)?.typeId,
          cityId: payload.city,
          removed: payload.removed,
          status: payload.status,
          timestamp: payload.timestamp,
          map: payload.Map,
          ownerName: payload.ownerName,
          ownerImageId: payload.ownerImageId,
          ownerGender: payload.ownerGender,
          familyInfo: payload.familyInfo,
          profit: payload.profit,
          feeAmount: payload.feeAmount,
          ownerCustomAvatar: payload.ownerCustomAvatar,
          ownerCustomBackground: payload.ownerCustomBackground,
        } as BusinessProfile;
      }
    );
    builder.addCase(getBusinessProfileAction.rejected, (state) => {
      state.loadingBusinessProfile = false;
    });
  },
});

export default businessSlice.reducer;
